import React, { useEffect, useState, useRef } from 'react';
import { Input, Dropdown, Button, AddRemove, toastMessage } from '../utils';
import { usePlantContext } from "../../context/plantContext";
import { useDevices } from '../../services/hooks';
import { buttonClasses } from "../utils/theme";
import { valid_email, valid_mobile } from '../../services/validation';
import { useUpdateDeviceAlertMutation } from '../../api/deviceSlice';
import { useUpdatePlantMutation } from "../../api/vmsSlice";
import { Tooltip } from "@mui/material";
import { ExpandMoreIcon, InfoIcon } from "../../services/icons";
import { useOrgs, usePlant, useProductCategories, useProducts, useSuppliers, useUserDetail } from '../../services/hooks';

const defaultAlert = {
            "id" : "",
            "displayName" : "",
            "alertConfig" : {
                "BoomOpen" : {alertThreshold: 0,
                cooldownPeriod: 0,
                notifyEmails: [],
                notifyMobiles: [],}
            }
}
const defaultForm = {
    alertConfig : [
		{
			filterType : "",
			filterValue : "",
			threshold: 10,
			startCheckpointNumber: 1,
			notifyEmails: [],
			notifyMobiles: []
		}
	]
}

const getDefaultForm = () => ({
    alertConfig: defaultForm.alertConfig.map((config) => ({
        ...config,
        notifyEmails: [...config.notifyEmails],
        notifyMobiles: [...config.notifyMobiles],
    })),
});

const itemTypeOptions = [
	{ label: "Product", value: "product" }, 
	{ label: "Product Category", value: "productCategory" }, 
	{ label: "Supplier", value: "supplier" }
];

const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};

export default function AlertConfig() {
    const { plantId, plantOptions, loading, updatePlantId, isCheckpointForm} = usePlantContext();
    const { userDetail, isSuperAdmin } = useUserDetail();
    const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
    const filterRef = useRef(defaultFilter);
    const { page_no = 1, pagesize = 20 } = filterRef.current;
    const { productCategories, message, getProdCats } = useProductCategories();
    const { products, count: productCount, getProducts } = useProducts();
    const { suppliers, count: supplierCount, getSuppliers } = useSuppliers();
    const { devices, count, isLoading, getDevices } = useDevices();
    const [handleUpdateDevice, { isLoading: isUpdating }] = useUpdateDeviceAlertMutation();
    const { plants, isLoading :isplantLoading, getPlantDetails, clearPlants: clearSelectedPlantDetails } = usePlant(undefined, "alertConfigSelectedPlant");
    const { plantOptions: orgPlantOptions, isLoading: isOrgPlantLoading, getPlantDetails: getOrgPlantDetails, clearPlants: clearOrgPlants } = usePlant(undefined, "alertConfigOrgPlants");
    const [handleUpdate] = useUpdatePlantMutation();
    // const [JbOpen, setJbOpen] = useState({ 
    //   alertThreshold: 0,
    //   cooldownPeriod: 0,
    //   notifyEmails: [],
    //   notifyMobiles: [],
    // });
    const [formData, setFormData] = useState(getDefaultForm);
    const [deviceList, setDeviceList] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState("");
    const orgValue = orgOptions.find((_) => _.value == selectedOrgId) || null;
    const warehouseOptions = isSuperAdmin ? orgPlantOptions : plantOptions;
    const selectedDefaultPlant = warehouseOptions.find((opt) => opt.value === plantId) || warehouseOptions[0] || null;
    const [selectedPlant, setSelectedPlant] = useState(selectedDefaultPlant);
    const selectedPlantData = plants.find((plant) => plant._id === selectedPlant?.value);
    const [error, setError] = useState({});

    const handleOrgChange = (option) => {
        const orgId = option?.value || "";
        setSelectedOrgId(orgId);
        setSelectedPlant(null);
        setDeviceList([]);
        setFormData(getDefaultForm());
        setError({});

        if (orgId) {
            getOrgPlantDetails({ orgId });
        } else {
            clearOrgPlants();
        }
    };

    useEffect(() => {
        if (isSuperAdmin) return;

        const defaultPlant = plantOptions.find((opt) => opt.value === plantId) || plantOptions[0] || null;
        setSelectedPlant((current) => (current?.value === defaultPlant?.value ? current : defaultPlant));
    }, [isSuperAdmin, plantId, plantOptions]);

    useEffect(() => {
        if (!isSuperAdmin) return;

        setSelectedPlant((current) => {
            const currentStillExists = orgPlantOptions.some((opt) => opt.value === current?.value);
            if (currentStillExists) return current;
            return orgPlantOptions[0] || null;
        });
    }, [isSuperAdmin, orgPlantOptions]);

    const handleAlertConfigChange = (value, name, id) => {
        if(true){
            setDeviceList((old) => old.map(item => 
                item.id === id ? {
                    ...item,
                    alertConfig: {
                        ...item.alertConfig,
                        BoomOpen: {
                            ...item.alertConfig.BoomOpen,
                            [name]: Array.isArray(value) ? value.map((_) => _.value) : value
                        }
                    }
                } : item
            ));
        }
    };
    const handleFormSubmit = async (formData) => {
        const validationErrors = validateFormData(formData);
        if (Object.keys(validationErrors).length > 0) {
            setError(validationErrors)
            return;
        }
        setError({})
        if (!selectedPlant?.value) {
            toastMessage(false, "Please select warehouse");
            return;
        }
        const {data: { error, message }} = await handleUpdateDevice({...formData, plantId: selectedPlant.value})
        if (error) {
            toastMessage(false, message);
        } else {
            toastMessage(true, message);
            // navigate("/devices", { state: { filterData: filterData || "" } });
        }
    };

    const handleWarehouseAlertConfigChange = (value, name, index) => {
        setFormData(prev => ({
            ...prev,
            alertConfig: prev.alertConfig.map((config, i) => {
                if (i !== index) return config;

                // If filterType changes → reset filterValue
                if (name === "filterType") {
                    return {
                        ...config,
                        filterType: value,
                        filterValue: "" // reset
                    };
                }

                return {
                    ...config,
                    [name]: Array.isArray(value)
                        ? value.map(v => v.value)
                        : value?.value ?? value
                };
            })
        }));
    };

    const addAlertConfig = () => {
		setFormData((old) => ({
			...old,
			alertConfig: [
				...old.alertConfig,
				...getDefaultForm().alertConfig
			],
		}));
	};

    const removeAlertConfig = (index) => {
		setFormData((old) => ({
			...old,
			alertConfig: old.alertConfig.filter((_, i) => i !== index),
		}));
	};

    useEffect(() => {
        if (selectedPlant?.value) {
            setFormData(getDefaultForm());
            setDeviceList([]);
            clearSelectedPlantDetails();

            const plantPayload = {
                ...filterRef.current,
                plantIds: selectedPlant.value,
                ...(isSuperAdmin && selectedOrgId ? { orgId: selectedOrgId } : {}),
            };

            getDevices({ pagesize: 1000, page_no: 1, plantIds: selectedPlant.value, deviceType: "rfid", ...(plantPayload.orgId ? { orgId: plantPayload.orgId } : {}) });
            getPlantDetails({ pagesize: 10, page_no: 1, plantIds: selectedPlant.value, projection : ["alertConfig"], ...(plantPayload.orgId ? { orgId: plantPayload.orgId } : {}) });
            getProducts(plantPayload);
            getProdCats(plantPayload);
            getSuppliers(plantPayload);
        } else {
            setDeviceList([]);
            setFormData(getDefaultForm());
        }
    }, [selectedPlant?.value, selectedOrgId, isSuperAdmin]);

    useEffect(()=>{
        if(devices?.length > 0){
            const deviceList = devices.filter((el) => el.deviceSubType ==="customReader" && el.hasOwnProperty('alertConfig') && Object.keys(el.alertConfig?.BoomOpen).length > 0)?.map((el) => ({id : el._id, "displayName" : el.displayName, "alertConfig" : el.alertConfig})) || [];
            setDeviceList(deviceList)
        }else{
            setDeviceList([])
        }
    }, [devices])

    useEffect(() => {
        if (!selectedPlant?.value || !selectedPlantData?.alertConfig) {
            setFormData(getDefaultForm());
            return;
        }

        const plantAlertConfig = selectedPlantData.alertConfig;

        // If backend gives object like { BoomOpen: {...} }
        // OR array → handle both safely

        let mappedConfigs = [];

        if (Array.isArray(plantAlertConfig)) {
            mappedConfigs = plantAlertConfig.map((item) => ({
                filterType: item.filterType || "",
                filterValue: item.filterValue || "",
                threshold: item.threshold || 10,
                startCheckpointNumber: item.startCheckpointNumber || 1,
                notifyEmails: item.notifyEmails || [],
                notifyMobiles: item.notifyMobiles || []
            }));
        } else {
            // fallback (if only BoomOpen exists)
            mappedConfigs = [
                {
                    filterType: "",
                    filterValue: "",
                    threshold: plantAlertConfig?.BoomOpen?.alertThreshold || 10,
                    startCheckpointNumber: 1,
                    notifyEmails: plantAlertConfig?.BoomOpen?.notifyEmails || [],
                    notifyMobiles: plantAlertConfig?.BoomOpen?.notifyMobiles || []
                }
            ];
        }

        setFormData({
            alertConfig: mappedConfigs
        });

    }, [selectedPlant?.value, selectedPlantData]);

    const getFilterValueOptions = (type) => {
        switch (type) {
            case "supplier":
                return suppliers
                    ?.filter(el => el.plantId === selectedPlant?.value)
                    ?.map(ele => ({ label: ele.supplierName, value: ele._id })) || [];

            case "product":
                return products
                    ?.filter(el => el.plantId === selectedPlant?.value)
                    ?.map(ele => ({ label: ele.productName, value: ele._id })) || [];

            case "productCategory":
                return productCategories
                    ?.filter(el => el.plantId === selectedPlant?.value)
                    ?.map(ele => ({ label: ele.productCategory, value: ele._id })) || [];

            default:
                return [];
        }
    };

    const handleWarehouseFormSubmit = async () => {

        const errors = {};

        if (isSuperAdmin && !selectedOrgId) {
            toastMessage(false, "Please select organization");
            return;
        }

        if (!selectedPlant?.value) {
            toastMessage(false, "Please select warehouse");
            return;
        }

        if (!selectedPlantData?._id) {
            toastMessage(false, "Warehouse details are not loaded yet");
            return;
        }

        formData.alertConfig.forEach((config, index) => {

            // ✅ Threshold validation
            if (!config.threshold || config.threshold <= 0) {
                errors[`threshold-${index}`] = "Alert threshold must be greater than 0";
            }

            // ✅ Either email OR mobile required
            if (
                (!config.notifyEmails || config.notifyEmails.length === 0) &&
                (!config.notifyMobiles || config.notifyMobiles.length === 0)
            ) {
                errors[`notifyEmails-${index}`] = "Provide at least one email or mobile";
                errors[`notifyMobiles-${index}`] = "Provide at least one email or mobile";
            }

            // ✅ filterType → filterValue required
            if (config.filterType && !config.filterValue) {
                errors[`filterValue-${index}`] = "Please select item";
            }
        });

        if (Object.keys(errors).length > 0) {
            setError(errors);
            return;
        }

        setError({});

        let updatePayload = {
            id: selectedPlantData?._id,
            updateData: {
                ...formData,
            },
        };

        const {
            data: { error, message },
        } = await handleUpdate(updatePayload);

        if (error) {
            toastMessage(false, message);
        } else {
            toastMessage(true, message);
        }
    };


    return (
        <div style={{ padding: "10px" , paddingTop: '20px' }}>
            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "nowrap", minWidth: "200px", width: "100%" }}>
                {isSuperAdmin ? (
                    <div style={{ width: "280px", flex: "0 0 280px" }}>
                        <Dropdown
                            id="orgId"
                            name="orgId"
                            label="Organization Name"
                            options={orgOptions || []}
                            value={orgValue}
                            onChange={handleOrgChange}
                        />
                    </div>
                ) : null}
                {(isSuperAdmin || (warehouseOptions && warehouseOptions.length > 1)) ? (
                    <div style={{ width: "280px", flex: "0 0 280px" }}>
                        <Dropdown
                            label="Select Warehouse"
                            options={isSuperAdmin && !selectedOrgId ? [] : warehouseOptions || []}
                            value={selectedPlant}
                            onChange={(value)=> setSelectedPlant(value) }
                            disabled={(isSuperAdmin && !selectedOrgId) || isOrgPlantLoading || loading || !warehouseOptions || warehouseOptions.length === 0}
                            // disableClearable={true}
                        />
                    </div>
                ) : null}
            </div>
            {deviceList.length > 0 && deviceList.map((device, index)=> (
                <div key={device.id || index}>
                    <h6 style={{ marginTop : '10px'}}>Boom Open Alert :</h6>
                    <div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
                        <div style={{ display: "flex", gap: "1rem",flexWrap: "wrap"}} className="p-3 border boder-rad-10p ">
                        <Input
                            label="Device Name"
                            value={device.displayName}
                            error={error.id}
                            readOnly
                        />
                        <Input
                            type="number"
                            id={`alertThreshold-${device.id}`}
                            name="alertThreshold"
                            value={device.alertConfig.BoomOpen.alertThreshold || 0}
                            label="Alert Threshold (sec)"
                            onChange={(e) => handleAlertConfigChange(e.target.value, e.target.name, device.id)}
                            required={true}
                            error={error.BoomOpen?.alertThreshold || error.BoomOpenAlertThreshold}
                            sx={{ width: '160px' }}
                        />
                        <Input
                            type="number"
                            id={`cooldownPeriod-${device.id}`}
                            name="cooldownPeriod"
                            value={device.alertConfig.BoomOpen.cooldownPeriod || 0}
                            label="Cooldown Period (sec)"
                            onChange={(e) => handleAlertConfigChange(e.target.value, e.target.name, device.id)}
                            required={true}
                            error={error.BoomOpen?.cooldownPeriod || error.BoomOpenCooldownPeriod}
                            sx={{ width: '160px' }}
                        />
                        <div style={{ minWidth: "245px" }}>
                            <Dropdown
                                id={`notifyEmails-${device.id}`}
                                name="notifyEmails"
                                label="Notify Emails"
                                onChange={(option, name) => handleAlertConfigChange(option, name, device.id)}
                                options={device.alertConfig.BoomOpen.notifyEmails?.length > 0 ? device.alertConfig.BoomOpen.notifyEmails.map((el) => ({ label: el, value: el })) : []}
                                value={device.alertConfig.BoomOpen.notifyEmails?.length > 0 ? device.alertConfig.BoomOpen.notifyEmails.map((el) => ({ label: el, value: el })) : []}
                                enableCustomValue={true}
                                validateValue={(value) => valid_email(value)}
                                error={error.BoomOpen?.notifyEmails || error.BoomOpenNotifyEmails}
                                multiple={true}
                                required
                            />
                        </div>
                        <div style={{ minWidth: "245px" }}>
                            <Dropdown
                                id={`notifyMobiles-${device.id}`}
                                name="notifyMobiles"
                                label="Notify Mobiles"
                                onChange={(option, name) => handleAlertConfigChange(option, name, device.id)}
                                options={device.alertConfig.BoomOpen.notifyMobiles?.length > 0 ? device.alertConfig.BoomOpen.notifyMobiles.map((el) => ({ label: el, value: el })) : []}
                                value={device.alertConfig.BoomOpen.notifyMobiles?.length > 0 ? device.alertConfig.BoomOpen.notifyMobiles.map((el) => ({ label: el, value: el })) : []}
                                enableCustomValue={true}
                                validateValue={(value) => valid_mobile(value)}
                                error={error.BoomOpen?.notifyMobiles || error.BoomOpenNotifyMobiles}
                                multiple={true}
                                required
                            />
                        </div>
                        {<Button text={"Update"} style={{ ...buttonClasses.lynkitOrangeFill, width: "80px", height : '38px' }} onClick={() => handleFormSubmit(device)} />}
                        </div>
                    </div>
                </div>
            ))}
            <div className="theme-container">
                <h6>Alert Configuration</h6>
                {formData.alertConfig.map((config, index) => (
                    <div key={`${selectedPlant?.value || "no-plant"}-${index}`} style={{ display: "flex", gap: "0.5rem", width: "100%", marginBottom : "10px" }}>
                        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }} className="p-3 border boder-rad-10p ">
                        {true && <><div style={{ display: "flex", alignItems: "center", minWidth: "220px" }}>
                            <Dropdown
                                id="filterType"
                                name="filterType"
                                value={
                                    config.filterType
                                        ? itemTypeOptions.find(opt => opt.value === config.filterType)
                                        : null
                                }
                                options={itemTypeOptions}
                                label="Item Type"
                                onChange={(option, name) =>
                                    handleWarehouseAlertConfigChange(option?.value, name, index)
                                }
                                error={error[`filterType-${index}`]}
                                
                            />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", minWidth: "220px" }}>
                            {/* <p>{config.filterValue}</p> */}
                            <Dropdown
                                id="filterValue"
                                name="filterValue"
                                value={
                                    config.filterValue
                                        ? getFilterValueOptions(config.filterType)
                                            .find(ele => ele.value === config.filterValue)
                                        : null
                                }
                                options={getFilterValueOptions(config.filterType)}
                                label="Item"
                                onChange={(option, name) =>
                                    handleWarehouseAlertConfigChange(option, name, index)
                                }
                                error={error[`filterValue-${index}`]}
                            />
                        </div></>}
                        <Input
                            type="number"
                            id={`Alert Threshold`}
                            name="threshold"
                            value={config.threshold}
                            label="Alert Threshold (sec)"
                            onChange={(e) => handleWarehouseAlertConfigChange(e.target.value, e.target.name, index)}
                            min="0"
                            error={error[`threshold-${index}`]}
                        />
                        <Input
                            type="number"
                            id={`startCheckpointNumber`}
                            name="startCheckpointNumber"
                            value={config.startCheckpointNumber || 1}
                            label="Start Checkpoint Number"
                            onChange={(e) => handleWarehouseAlertConfigChange(e.target.value, e.target.name, index)}
                            min="1"
                        />
                        <div style={{ display: "flex", alignItems: "center", minWidth: "280px" }}>
                            <Dropdown
                            id={`notifyEmails`}
                            name="notifyEmails"
                            label="Notify Emails"
                            onChange={(option, name) => handleWarehouseAlertConfigChange(option, name, index)}
                            options={config.notifyEmails?.length > 0 ? config.notifyEmails.map((el) => ({ label: el, value: el })) : []}
                            value={config.notifyEmails?.length > 0 ? config.notifyEmails.map((el) => ({ label: el, value: el })) : []}
                            enableCustomValue={true}
                            validateValue={(value) => valid_email(value)}
                            multiple={true}
                            error={error[`notifyEmails-${index}`]}
                            />
                            <Tooltip style={{ marginLeft: "1px" }} title="Type email and press Enter to add to list. Multiple emails supported.">
                            <InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
                            </Tooltip>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", minWidth: "280px" }}>
                            <Dropdown
                            id={`notifyMobiles`}
                            name="notifyMobiles"
                            label="Notify Mobiles"
                            onChange={(option, name) => handleWarehouseAlertConfigChange(option, name, index)}
                            options={config.notifyMobiles?.length > 0 ? config.notifyMobiles.map((el) => ({ label: el, value: el })) : []}
                            value={config.notifyMobiles?.length > 0 ? config.notifyMobiles.map((el) => ({ label: el, value: el })) : []}
                            enableCustomValue={true}
                            validateValue={(value) => valid_mobile(value)}
                            multiple={true}
                            error={error[`notifyMobiles-${index}`]}
                            />
                            <Tooltip style={{ marginLeft: "1px" }} title="Type mobile and press Enter to add to list. Multiple mobile number supported.">
                            <InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
                            </Tooltip>
                        </div>
                        <AddRemove list={formData.alertConfig} filterMethod={() => false} onAdd={addAlertConfig} onRemove={removeAlertConfig} index={index} outerIndex={index} />
                        </div>
                    </div>
                ))}
                <div className="" style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "1rem" }}>
                    <Button text={"Update"} style={{ ...buttonClasses.lynkitOrangeFill, width: "15%" }} onClick={handleWarehouseFormSubmit} />
                </div>
            </div>
        </div>
        
    );
}


const validateFormData = (formData) => {
    const errors = {};

    // Validate ID
    if (!formData.id || formData.id.trim() === '') {
        errors.id = 'Device ID is required';
    }

    // Validate displayName
    if (!formData.displayName || formData.displayName.trim() === '') {
        errors.displayName = 'Device name is required';
    }

    // Validate alertConfig exists
    if (!formData.alertConfig || typeof formData.alertConfig !== 'object') {
        errors.alertConfig = 'Alert configuration is required';
        return errors; // Return early if no alertConfig
    }

    // Validate each alert type (BoomOpen, JbOpen, etc.)
    Object.entries(formData.alertConfig).forEach(([alertType, alertConfig]) => {
        const alertErrors = {};
        // Validate alertThreshold
        if (!alertConfig.alertThreshold || alertConfig.alertThreshold == 0) {
            alertErrors.alertThreshold = `Alert threshold is required`;
        } else if (isNaN(alertConfig.alertThreshold) || Number(alertConfig.alertThreshold) < 0) {
            alertErrors.alertThreshold = `Alert threshold must be a positive number`;
        }

        // Validate cooldownPeriod
        if (!alertConfig.cooldownPeriod || alertConfig.cooldownPeriod == 0) {
            alertErrors.cooldownPeriod = `Cooldown period is required`;
        } else if (isNaN(alertConfig.cooldownPeriod) || Number(alertConfig.cooldownPeriod) < 0) {
            alertErrors.cooldownPeriod = `Cooldown period must be a positive number`;
        }

        // Validate notifyEmails
        // if (!alertConfig.notifyEmails || !Array.isArray(alertConfig.notifyEmails)) {
        //     alertErrors.notifyEmails = `Notify emails must be an array`;
        // } else {
        //     alertConfig.notifyEmails.forEach((email, index) => {
        //         if (!valid_email(email)) {
        //             if (!alertErrors.notifyEmails) alertErrors.notifyEmails = [];
        //             alertErrors.notifyEmails[index] = 'Invalid email format';
        //         }
        //     });
        //     // If all emails are valid but array is empty
        //     if (alertConfig.notifyEmails.length === 0) {
        //         alertErrors.notifyEmails = 'At least one email is required';
        //     }
        // }

        // Validate notifyMobiles
        // if (!alertConfig.notifyMobiles || !Array.isArray(alertConfig.notifyMobiles)) {
        //     alertErrors.notifyMobiles = `Notify mobiles must be an array`;
        // } else {
        //     alertConfig.notifyMobiles.forEach((mobile, index) => {
        //         if (!valid_mobile(mobile)) {
        //             if (!alertErrors.notifyMobiles) alertErrors.notifyMobiles = [];
        //             alertErrors.notifyMobiles[index] = 'Invalid mobile number format';
        //         }
        //     });
        //     // If all mobiles are valid but array is empty
        //     if (alertConfig.notifyMobiles.length === 0) {
        //         alertErrors.notifyMobiles = 'At least one mobile number is required';
        //     }
        // }

        // If there are errors for this alert type, add them to main errors
        if (Object.keys(alertErrors).length > 0) {
            errors[alertType] = alertErrors;
        }
    });

    return errors;
};
